"use client";

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const lastUpdatedDate = "2026-02-26";

  const renderList = (key: string) => {
    const items = t(key, { returnObjects: true });
    if (!Array.isArray(items)) return null;
    return (
      <ul className="list-disc pl-5 space-y-1 my-2 text-muted-foreground">
        {items.map((item, i) => (
          <li key={i} className="leading-relaxed">
            {String(item)}
          </li>
        ))}
      </ul>
    );
  };

  const Section = ({
    titleKey,
    children,
  }: {
    titleKey: string;
    children: React.ReactNode;
  }) => (
    <section className="mb-8">
      <h2 className="font-display text-lg font-semibold text-foreground mb-3">
        {String(t(titleKey))}
      </h2>
      <div className="text-muted-foreground leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );

  const SubSection = ({
    titleKey,
    children,
  }: {
    titleKey: string;
    children: React.ReactNode;
  }) => (
    <div className="mt-4 ml-0">
      <h3 className="font-display text-base font-semibold text-foreground mb-2">
        {String(t(titleKey))}
      </h3>
      <div className="text-muted-foreground leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  );

  const P = ({ keyName }: { keyName: string }) => (
    <p>{String(t(keyName))}</p>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="pt-28 pb-16 md:pt-32 md:pb-20">
          <div className="container mx-auto px-4 md:px-8 max-w-3xl">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" />
              {String(t("sc.backHome"))}
            </Link>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-2">
              {String(t("footer.privacyPolicy"))}
            </h1>
            <p className="text-sm text-muted-foreground mb-10">
              {String(t("privacy.lastUpdated"))}: {lastUpdatedDate}
            </p>

            <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none text-foreground">
              <Section titleKey="privacy.section1Title">
                <P keyName="privacy.section1P1" />
                {renderList("privacy.section1List1")}
                <P keyName="privacy.section1P2" />
              </Section>

              <Section titleKey="privacy.section2Title">
                <p>{String(t("privacy.section2Company"))}</p>
                <p>{String(t("privacy.section2Address"))}</p>
                <p>{String(t("privacy.section2Siren"))}</p>
                <p>{String(t("privacy.section2Vat"))}</p>
                <p>{String(t("privacy.section2Email"))}</p>
                <p className="mt-3">{String(t("privacy.section2Dpo"))}</p>
                <p>{String(t("privacy.section2DpoEmail"))}</p>
              </Section>

              <Section titleKey="privacy.section3Title">
                <SubSection titleKey="privacy.section3_1Title">
                  <P keyName="privacy.section3_1P1" />
                  {renderList("privacy.section3_1List")}
                </SubSection>
                <SubSection titleKey="privacy.section3_2Title">
                  <P keyName="privacy.section3_2P1" />
                  {renderList("privacy.section3_2List")}
                  <P keyName="privacy.section3_2P2" />
                </SubSection>
              </Section>

              <Section titleKey="privacy.section4Title">
                <SubSection titleKey="privacy.section4_1Title">
                  {renderList("privacy.section4_1List")}
                </SubSection>
                <SubSection titleKey="privacy.section4_2Title">
                  {renderList("privacy.section4_2List")}
                </SubSection>
                <SubSection titleKey="privacy.section4_3Title">
                  <P keyName="privacy.section4_3P1" />
                  {renderList("privacy.section4_3List1")}
                  <P keyName="privacy.section4_3P2" />
                  {renderList("privacy.section4_3List2")}
                </SubSection>
              </Section>

              <Section titleKey="privacy.section5Title">
                <P keyName="privacy.section5P1" />
                {renderList("privacy.section5List")}
                <P keyName="privacy.section5P2" />
              </Section>

              <Section titleKey="privacy.section6Title">
                <P keyName="privacy.section6P1" />
                {renderList("privacy.section6List")}
              </Section>

              <Section titleKey="privacy.section7Title">
                <p className="font-medium text-foreground">{String(t("privacy.section7TableIntro"))}</p>
                <ul className="list-none pl-0 space-y-1 my-2">
                  <li>{String(t("privacy.section7Table1"))}</li>
                  <li>{String(t("privacy.section7Table2"))}</li>
                  <li>{String(t("privacy.section7Table3"))}</li>
                  <li>{String(t("privacy.section7Table4"))}</li>
                </ul>
                <P keyName="privacy.section7P1" />
                {renderList("privacy.section7List")}
                <P keyName="privacy.section7P2" />
              </Section>

              <Section titleKey="privacy.section8Title">
                <P keyName="privacy.section8P1" />
                {renderList("privacy.section8List")}
              </Section>

              <Section titleKey="privacy.section9Title">
                <P keyName="privacy.section9P1" />
                {renderList("privacy.section9List")}
              </Section>

              <Section titleKey="privacy.section10Title">
                <P keyName="privacy.section10P1" />
                <P keyName="privacy.section10P2" />
              </Section>

              <Section titleKey="privacy.section11Title">
                <P keyName="privacy.section11P1" />
                {renderList("privacy.section11List")}
                <P keyName="privacy.section11P2" />
                <P keyName="privacy.section11P3" />
                <P keyName="privacy.section11P4" />
              </Section>

              <Section titleKey="privacy.section12Title">
                <P keyName="privacy.section12P1" />
                {renderList("privacy.section12List")}
              </Section>

              <Section titleKey="privacy.section13Title">
                <P keyName="privacy.section13P1" />
                {renderList("privacy.section13List")}
              </Section>

              <Section titleKey="privacy.section14Title">
                <P keyName="privacy.section14P1" />
                <P keyName="privacy.section14P2" />
              </Section>

              <Section titleKey="privacy.section15Title">
                <P keyName="privacy.section15P1" />
                <P keyName="privacy.section15P2" />
              </Section>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
